import React from 'react';
import { Modal } from '../../../components/common/Modal';
import { Button } from '../../../components/common/Button';
import { Clock, ArrowLeft } from 'lucide-react';

interface SubscriptionMinutesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSeeFeedback: () => void;
  onBackToDashboard: () => void;
  elapsedTimeInSeconds: number;
}

const SubscriptionMinutesModal: React.FC<SubscriptionMinutesModalProps> = ({
  isOpen,
  onClose,
  onSeeFeedback,
  onBackToDashboard,
  elapsedTimeInSeconds
}) => {
  // Check if interview was shorter than 1 minute (60 seconds)
  const isInterviewTooShort = elapsedTimeInSeconds < 60;
  
  // Prevent closing with escape key or clicking outside by overriding close behavior
  const handleClose = () => {
    console.log('[SubscriptionMinutesModal] Close attempted, calling appropriate action');
    
    // If interview is too short, go back to dashboard, otherwise generate feedback
    if (isInterviewTooShort) {
      onBackToDashboard();
    } else {
      onSeeFeedback();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose} // Always call appropriate action when close is attempted
      title="Subscription Minutes Consumed"
      maxWidth="450px"
      hideCloseButton={true} // Hide the X button to prevent closing
    >
      <div className="p-6 space-y-4">
        <div className="flex flex-col items-center justify-center mb-4">
          <div className="bg-primary-light/20 dark:bg-primary-dark/20 p-3 rounded-full mb-3">
            <Clock size={32} className="text-primary-light dark:text-primary-dark" />
          </div>
          <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark text-center">
            Your Subscription Minutes Are Up
          </h3>
        </div>

        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark text-center">
          You've used all available minutes in your subscription. We'll need to end this interview now.
        </p>
        
        {isInterviewTooShort ? (
          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark text-center">
            Since your interview was less than 1 minute long, we can't generate meaningful feedback. 
          </p>
        ) : (
          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark text-center">
            Don't worry, you can still receive detailed feedback on your performance so far.
          </p>
        )}

        <div className="flex flex-col md:flex-row gap-3 justify-center mt-6">
          {isInterviewTooShort ? (
            <Button 
              variant="outline" 
              onClick={onBackToDashboard}
              className="w-full"
              leftIcon={<ArrowLeft size={16} />}
            >
              Back to Dashboard
            </Button>
          ) : (
            <Button 
              variant="primary" 
              onClick={onSeeFeedback}
              className="w-full"
            >
              Generate Feedback
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default SubscriptionMinutesModal; 